import React, { useState } from 'react';
import { Briefcase, Code, FileText, Linkedin, ExternalLink, ChevronRight } from 'lucide-react';
import Resume from './Resume';
import OpEd from './OpEd';

const Portfolio = () => {
  const [activeSection, setActiveSection] = useState('projects');
  const [showResume, setShowResume] = useState(false);
  const [showOpEd, setShowOpEd] = useState(null);

  // Sample data - replace with your actual content
  const projects = [
    {
      title: "Flint",
      description: "Built a smart task management app using Claude API that auto-categorizes and prioritizes tasks based on natural language input.",
      tech: ["React", "Node.js", "Claude API"],
      link: "#",
      image: "⚡"
    },
    {
      title: "RunningTunes",
      description: "Created a music companion app for runners that matches song tempo to running pace for the perfect workout soundtrack.",
      tech: ["React Native", "Spotify API", "TypeScript"],
      link: "#",
      image: "🎵"
    }
  ];

  const internships = [
    {
      company: "Major League Baseball",
      role: "Product Management Intern",
      duration: "June 2025 - August 2025",
      description: "Worked on product development and user experience initiatives for MLB's digital platforms.",
      achievements: [
        "Designed and validated an end-to-end user experience for a 'Preview-to-Purchase' mid-game conversion flow for MLB.TV on the MLB app, collaborating with Design, Engineering, Analytics, Streaming, and Business",
        "Led 20 UX research interviews and designed mockups, translating insights into a product recommendation for stakeholders",
        "Analyzed over 2,000 UX survey results and user engagement data from the 2025 All Star ballot and created an actionable redesign recommendation for the 2026 All Star Ballot"
      ]
    },
    {
      company: "WeightWatchers (WW)",
      role: "UXR Intern",
      duration: "May 2024 - August 2024",
      description: "Conducted user experience research to inform product development and design decisions.",
      achievements: [
        "Planned and conducted 6 UX research projects throughout 10-week internship using data from self-conducted interviews, AI-moderated interviews, focus groups, diary studies, and surveys",
        "Collaborated with Product, Design, and Engineering to translate research findings into user-centric product solutions",
        "Created PowerPoint and Google Doc reports delivering actionable recommendations for product teams and presented the final findings to an audience of over 50 WW employees"
      ]
    },
    {
      company: "A.Team",
      role: "Evaluations Intern",
      duration: "June 2023 - August 2023",
      description: "Worked on technical evaluation processes and data management for the team formation platform.",
      achievements: [
        "Implemented a virtual technical test for the team formation platform, to heighten the standards for their developers aiming to save human resources by automating a portion of the evaluation process for new developers",
        "Orchestrated the seamless migration of data from over 6,000 developer profiles on Notion to the A.Team platform, enabling the team to leverage the dataset for trend analysis and continuous improvement initiatives"
      ]
    },
    {
      company: "TAMID Group at Duke",
      role: "President",
      duration: "December 2024 - Present",
      description: "Leading a cross-functional finance, technology, and consulting organization.",
      achievements: [
        "Leading cross-functional finance, technology, and consulting organization with 116+ members, managing executive team priorities and resource allocation through weekly leadership meetings",
        "Organizing biweekly general body meetings including in-person speaker events with David Zalik (CEO of GreenSky), Caryn Seidman-Becker (CEO of CLEAR), and Alan Wise (Senior Partner at BCG)",
        "Owned fall recruitment pipeline, defining evaluation criteria and managing funnel from 100+ applications to selection"
      ]
    }
  ];

  const blogPosts = [
    {
      title: "The Concert You'll Never See",
      date: "Jan 2026",
      excerpt: "How ticket bots are preventing real fans from seeing their favorite artists, and why the BOTS Act isn't working...",
      readTime: "3 min read",
      id: "concert-oped"
    },
    {
      title: "Year of Analog",
      date: "Jan 2026",
      excerpt: "How the Year of Analog movement is creating space for a new category of products designed to protect, guide, and enrich attention rather than capture it...",
      readTime: "4 min read",
      id: "year-of-analog"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Animated background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
      </div>

      <div className="relative z-10">
        {/* Hero Section */}
        <header className="relative w-full overflow-hidden pt-10 md:pt-12">
          {/* Banner with Image and Light Blue Background */}
          <div className="relative w-full bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex justify-center">
            <div className="relative w-full max-w-[95%] md:max-w-[90%] lg:max-w-[85%]">
              <img 
                src="/images/golden-gate-bridge-header.jpg" 
                alt="Golden Gate Bridge"
                className="w-full h-auto object-contain block"
              />
              {/* Overlay for better text readability */}
              <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/60 to-transparent"></div>
              
              {/* Content Overlay */}
              <div className="absolute inset-0 z-10 flex items-center">
                <div className="container mx-auto px-6 md:px-12">
                  <div className="max-w-2xl">
                    <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold mb-6 text-white leading-tight">
                      Sylvia Jacoby
                    </h1>
                    <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed">
                      Technical product manager with a background in UX research. Duke class of 2026.
                    </p>
                    <div className="flex flex-wrap gap-4">
                      <a href="mailto:sylvia.jacoby@duke.edu" className="group flex items-center gap-2 px-6 py-3 bg-white hover:bg-white/90 text-slate-950 font-semibold rounded-full transition-all duration-300 hover:shadow-lg">
                        Get in Touch
                        <ChevronRight size={20} className="group-hover:translate-x-1 transition-transform" />
                      </a>
                      <button onClick={() => setShowResume(true)} className="flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white border border-white/30 font-semibold rounded-full transition-all duration-300 backdrop-blur-sm">
                        <FileText size={20} />
                        Resume
                      </button>
                      <a href="https://www.linkedin.com/in/sylvia-jacoby/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white border border-white/30 font-semibold rounded-full transition-all duration-300 backdrop-blur-sm">
                        <Linkedin size={20} />
                        LinkedIn
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Navigation */}
        <nav className="sticky top-0 z-50 bg-slate-900/80 backdrop-blur-xl border-b border-slate-800">
          <div className="container mx-auto px-6">
            <div className="flex gap-1 py-2">
              {[
                { id: 'projects', label: 'Projects', icon: Code },
                { id: 'internships', label: 'Experience', icon: Briefcase },
                { id: 'blog', label: 'Blog', icon: FileText }
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveSection(id)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                    activeSection === id
                      ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                      : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                  }`}
                >
                  <Icon size={18} />
                  {label}
                </button>
              ))}
            </div>
          </div>
        </nav>

        {/* Content Sections */}
        <main className="container mx-auto px-6 py-16">
          {/* Vibe Coded Projects */}
          {activeSection === 'projects' && (
            <section className="animate-fadeIn">
              <div className="mb-12">
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                  Projects I've Built
                </h2>
                <p className="text-xl text-slate-400">
                  Side projects where I shipped code and learned by doing
                </p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {projects.map((project, idx) => (
                  <div
                    key={idx}
                    className="group bg-slate-800/40 backdrop-blur-sm border border-slate-700 rounded-2xl p-6 hover:border-cyan-500/50 transition-all duration-500 hover:shadow-xl hover:shadow-cyan-500/10 hover:-translate-y-1"
                    style={{animationDelay: `${idx * 100}ms`}}
                  >
                    <div className="text-5xl mb-4">{project.image}</div>
                    <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-cyan-400 transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-slate-300 mb-4 leading-relaxed">
                      {project.description}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {project.tech.map((tech, i) => (
                        <span key={i} className="px-3 py-1 bg-slate-700/50 text-cyan-400 text-sm rounded-full border border-slate-600">
                          {tech}
                        </span>
                      ))}
                    </div>
                    <a href={project.link} className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 font-medium group/link">
                      View Project
                      <ExternalLink size={16} className="group-hover/link:translate-x-1 group-hover/link:-translate-y-1 transition-transform" />
                    </a>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Internships */}
          {activeSection === 'internships' && (
            <section className="animate-fadeIn">
              <div className="mb-12">
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                  Work Experience
                </h2>
                <p className="text-xl text-slate-400">
                  Where I've learned and made impact
                </p>
              </div>
              <div className="space-y-6">
                {internships.map((exp, idx) => (
                  <div
                    key={idx}
                    className="bg-slate-800/40 backdrop-blur-sm border border-slate-700 rounded-2xl p-8 hover:border-violet-500/50 transition-all duration-500"
                    style={{animationDelay: `${idx * 100}ms`}}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                      <div>
                        <h3 className="text-3xl font-bold text-white mb-2">
                          {exp.company}
                        </h3>
                        <div className="flex flex-wrap items-center gap-3 text-slate-400">
                          <span className="text-lg font-medium text-cyan-400">{exp.role}</span>
                          <span className="text-slate-600">•</span>
                          <span>{exp.duration}</span>
                        </div>
                      </div>
                    </div>
                    <p className="text-slate-300 text-lg mb-6 leading-relaxed">
                      {exp.description}
                    </p>
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">
                        Key Achievements
                      </p>
                      {exp.achievements.map((achievement, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-cyan-400"></div>
                          <p className="text-slate-300">{achievement}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Blog */}
          {activeSection === 'blog' && (
            <section className="animate-fadeIn">
              <div className="mb-12">
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                  Blog Posts
                </h2>
                <p className="text-xl text-slate-400">
                  Thoughts on product, tech, and building
                </p>
              </div>
              <div className="space-y-6">
                {blogPosts.map((post, idx) => (
                  <article
                    key={idx}
                    onClick={() => (post.id === 'concert-oped' || post.id === 'year-of-analog') && setShowOpEd(post.id)}
                    className="group bg-slate-800/40 backdrop-blur-sm border border-slate-700 rounded-2xl p-8 hover:border-violet-500/50 transition-all duration-500 hover:shadow-xl hover:shadow-violet-500/10 cursor-pointer"
                    style={{animationDelay: `${idx * 100}ms`}}
                  >
                    <div className="flex items-center gap-3 text-sm text-slate-500 mb-3">
                      <time>{post.date}</time>
                      <span>•</span>
                      <span>{post.readTime}</span>
                    </div>
                    <h3 className="text-3xl font-bold text-white mb-3 group-hover:text-violet-400 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-slate-300 text-lg leading-relaxed mb-4">
                      {post.excerpt}
                    </p>
                    <span className="inline-flex items-center gap-2 text-violet-400 font-medium group-hover:gap-3 transition-all">
                      Read More
                      <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  </article>
                ))}
              </div>
            </section>
          )}
        </main>

        {/* Footer */}
        <footer className="border-t border-slate-800 mt-24">
          <div className="container mx-auto px-6 py-12">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              <p className="text-slate-400">
                © 2026 Sylvia. Built with React & Tailwind CSS
              </p>
              <div className="flex gap-6">
                <a href="mailto:sylvia.jacoby@duke.edu" className="text-slate-400 hover:text-cyan-400 transition-colors">
                  Email
                </a>
                <a href="https://www.linkedin.com/in/sylvia-jacoby/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-cyan-400 transition-colors">
                  LinkedIn
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.6s ease-out forwards;
        }
      `}</style>
      {showResume && <Resume onClose={() => setShowResume(false)} />}
      {showOpEd && <OpEd articleId={showOpEd} onClose={() => setShowOpEd(null)} />}
    </div>
  );
};

export default Portfolio;
